import type { Formula, PercentOfSpec } from '@finsim/model';
import { useTranslation } from 'react-i18next';
import { ScheduledNumberField } from '../../../entities/project';
import { Field, OptionalMoneyField, PercentField, Section, Segmented } from '../../../shared/ui';
import { convertFormulaSpec, withOptional } from '../lib';
import { AccountField, CategoryField, DirectionField, WindowFields } from './common-fields';
import type { FormContext } from './context';
import { SourcesField } from './sources-field';
import { TaxBreakdown, TaxSections } from './tax-form';

export function FormulaForm({ item, ctx }: { item: Formula; ctx: FormContext }) {
  const { t } = useTranslation();
  const set = (next: Formula, field: string): void => {
    ctx.commit(next, field);
  };
  const spec = item.spec;
  const isTax = spec.kind === 'tax';

  return (
    <>
      <Section title={t('inspector.sections.formula')}>
        <Field label={t('fields.ruleKind')}>
          {() => (
            <Segmented
              label={t('fields.ruleKind')}
              value={spec.kind}
              onValueChange={(kind) => {
                set(
                  {
                    ...item,
                    // Taxes are always paid out.
                    ...(kind === 'tax' ? { direction: 'out' as const } : {}),
                    spec: convertFormulaSpec(spec, kind, item.start),
                  },
                  'kind',
                );
              }}
              options={[
                { value: 'percentOf', label: t('fields.ruleKinds.percentOf') },
                { value: 'tax', label: t('fields.ruleKinds.tax') },
              ]}
            />
          )}
        </Field>
        {!isTax && (
          <DirectionField
            value={item.direction}
            onChange={(direction) => {
              set({ ...item, direction }, 'direction');
            }}
          />
        )}
        <SourcesField
          item={item}
          ctx={ctx}
          onChange={(sources) => {
            set({ ...item, spec: { ...spec, sources } }, 'sources');
          }}
        />
        {spec.kind === 'percentOf' && (
          <PercentOfFields
            spec={spec}
            ctx={ctx}
            onChange={(next, field) => {
              set({ ...item, spec: next }, field);
            }}
          />
        )}
      </Section>

      {spec.kind === 'tax' && (
        <>
          <TaxBreakdown item={item} spec={spec} ctx={ctx} />
          <TaxSections
            spec={spec}
            ctx={ctx}
            onChange={(next, field) => {
              set({ ...item, spec: next }, field);
            }}
          />
        </>
      )}

      <Section title={t('inspector.sections.timing')}>
        <WindowFields ctx={ctx} item={item} onChange={set} />
      </Section>

      <Section title={t('inspector.sections.classification')}>
        {!isTax && (
          <AccountField
            ctx={ctx}
            value={item.account}
            onChange={(account) => {
              set(withOptional(item, 'account', account), 'account');
            }}
          />
        )}
        {(isTax || item.account === undefined) && (
          <CategoryField
            ctx={ctx}
            value={item.category}
            kind={item.direction === 'in' ? 'income' : 'expense'}
            onChange={(category) => {
              set(withOptional(item, 'category', category as Formula['category']), 'category');
            }}
          />
        )}
      </Section>
    </>
  );
}

function PercentOfFields({
  spec,
  ctx,
  onChange,
}: {
  spec: PercentOfSpec;
  ctx: FormContext;
  onChange: (spec: PercentOfSpec, field: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ScheduledNumberField
        label={t('fields.percent')}
        value={spec.percent}
        project={ctx.project}
        format={ctx.percent}
        onCommit={(percent) => {
          onChange({ ...spec, percent }, 'percent');
        }}
        renderInput={(value, onCommit, label, id) => (
          <PercentField
            id={id}
            aria-label={label}
            {...(id === undefined ? {} : { className: 'primary-field' })}
            value={value}
            min={0}
            max={1000}
            onCommit={onCommit}
          />
        )}
      />
      <Field label={t('fields.floor')}>
        {({ id }) => (
          <OptionalMoneyField
            id={id}
            value={spec.floor}
            symbol={ctx.symbol}
            placeholder={t('fields.noLimit')}
            onCommit={(floor) => {
              onChange(withOptional(spec, 'floor', floor), 'floor');
            }}
          />
        )}
      </Field>
      <Field label={t('fields.cap')}>
        {({ id }) => (
          <OptionalMoneyField
            id={id}
            value={spec.cap}
            symbol={ctx.symbol}
            placeholder={t('fields.noLimit')}
            onCommit={(cap) => {
              onChange(withOptional(spec, 'cap', cap), 'cap');
            }}
          />
        )}
      </Field>
    </>
  );
}
